import get from "lodash.get";
import ParkkiTheme from "themes/ParkkiTheme";
import CommonUtils from "services/common/utils";
import Abilities from "services/permissions/abilities";

class VisitorCharacteristicsUtils {
  static formatAreaFromApi = (area, config = []) => {
    const areaConfig = config.find((item) => {
      return (
        get(area, "id", get(area, "area_id")) ===
        get(item, "area_id", get(item, "id"))
      );
    });

    return {
      ...areaConfig,
      ...area,
    };
  };

  static formatAreasFromApi = (areas = [], config) => {
    return areas.map((area) =>
      VisitorCharacteristicsUtils.formatAreaFromApi(area, config)
    );
  };

  static formatLayersInfosFromApi = (
    layersInfos,
    config,
    currentSelectedArea = []
  ) => {
    const areas = VisitorCharacteristicsUtils.formatAreasFromApi(
      get(layersInfos, "areas", []),
      get(config, "areas", [])
    );

    return {
      ...layersInfos,
      areas,
      selectedArea:
        currentSelectedArea?.length > 0 ? currentSelectedArea : areas[0]?.id,
    };
  };

  static getSelectArea = (areas, selectedAreaId) => {
    return areas.find((area) => area?.id === selectedAreaId);
  };

  static getQualificationIndicatorsRow = (stats, intl) => {
    if (!Abilities.ability.can("read", "visitorCharacteristics.visitorStats"))
      return null;

    return [
      {
        toggle: true,
        headerColor: ParkkiTheme.colors.blue,
        label:
          intl.messages[
            "visitorCharacteristicsScene.sections.qualificationIndicators.visitorOnSite"
          ],
        subLabel: "",
        labelInfos:
          intl.messages[
            "visitorCharacteristicsScene.sections.qualificationIndicators.visitorOnSiteTooltip"
          ],
        value: {
          value: get(stats, "total_visitors", "-"),
          formatValue: (val) => val,
          formatNormal: (val) => val,
        },
        type: {
          name: "normal",
        },
      },
      {
        toggle: true,
        headerColor: ParkkiTheme.colors.turquoise,
        label:
          intl.messages[
            "visitorCharacteristicsScene.sections.qualificationIndicators.singleVisitorOnSite"
          ],
        subLabel: "",
        labelInfos:
          intl.messages[
            "visitorCharacteristicsScene.sections.qualificationIndicators.singleVisitorOnSiteTooltip"
          ],
        value: {
          value: get(stats, "unique_visitors", "-"),
          formatValue: (val) => val,
          formatNormal: (val) => val,
        },
        type: {
          name: "normal",
        },
      },
      {
        toggle: true,
        headerColor: ParkkiTheme.colors.orange,
        label: intl.formatMessage(
          {
            id: "visitorCharacteristicsScene.sections.qualificationIndicators.bounce",
          },
          {
            bounceValue: get(stats, "bounce_maximum_duration_in_minutes", "-"),
          }
        ),
        subLabel: "",
        labelInfos:
          intl.messages[
            "visitorCharacteristicsScene.sections.qualificationIndicators.bounceTooltip"
          ],
        value: {
          value: get(stats, "bounces", "-"),
          formatValue: (val) => val,
          formatNormal: (val) => val,
        },
        type: {
          name: "normal",
        },
      },
    ];
  };

  static getQualificationVisitorsChart = (stats, intl) => {
    if (
      !Abilities.ability.can(
        "read",
        "visitorCharacteristics.visitorDistribution"
      )
    ) {
      return null;
    }

    return {
      visitors: {
        data: {
          labels: [
            intl.messages[
              "visitorCharacteristicsScene.sections.qualificationVisitors.newVisitors"
            ],
            intl.messages[
              "visitorCharacteristicsScene.sections.qualificationVisitors.knownVisitors"
            ],
          ],
          datasets: [
            {
              backgroundColor: [
                ParkkiTheme.colors.turquoise3,
                ParkkiTheme.colors.blue,
              ],
              data: [stats?.new_visitors || 0, stats?.known_visitors || 0],
            },
          ],
        },
        options: {
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const dataset = get(data, "datasets[0]");

                const label = get(data, `labels[${get(tooltipItem, "index")}]`);
                const percentage = Math.round(
                  (get(dataset, `data[${get(tooltipItem, "index")}]`) /
                    get(
                      dataset,
                      `_meta[${get(
                        Object.keys(get(dataset, `_meta`)),
                        "[0]"
                      )}].total`
                    )) *
                    100
                );

                return ` ${label} : ${percentage} %`;
              },
              afterLabel: (tooltipItem, data) => "",
            },
          },
          legend: {
            display: false,
          },
          responsive: true,
          maintainAspectRatio: true,
        },
        legend: [
          {
            label:
              intl.messages[
                "visitorCharacteristicsScene.sections.qualificationVisitors.newVisitors"
              ],
            color: ParkkiTheme.colors.turquoise3,
          },
          {
            label:
              intl.messages[
                "visitorCharacteristicsScene.sections.qualificationVisitors.knownVisitors"
              ],
            color: ParkkiTheme.colors.blue,
          },
        ],
      },
      categorizedVisitors: {
        data: {
          labels: [
            intl.messages[
              "visitorCharacteristicsScene.sections.qualificationVisitors.daily"
            ],
            intl.messages[
              "visitorCharacteristicsScene.sections.qualificationVisitors.monthly"
            ],
            intl.messages[
              "visitorCharacteristicsScene.sections.qualificationVisitors.weekly"
            ],
            intl.messages[
              "visitorCharacteristicsScene.sections.qualificationVisitors.yearly"
            ],
          ],
          datasets: [
            {
              backgroundColor: [
                ParkkiTheme.colors.red,
                ParkkiTheme.colors.darkOrange,
                ParkkiTheme.colors.orange,
                ParkkiTheme.colors.yellow,
              ],
              data: [
                stats?.daily || 0,
                stats?.monthly || 0,
                stats?.weekly || 0,
                stats?.yearly || 0,
              ],
            },
          ],
        },
        options: {
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const dataset = get(data, "datasets[0]");

                const label = get(data, `labels[${get(tooltipItem, "index")}]`);
                const percentage = Math.round(
                  (get(dataset, `data[${get(tooltipItem, "index")}]`) /
                    get(
                      dataset,
                      `_meta[${get(
                        Object.keys(get(dataset, `_meta`)),
                        "[0]"
                      )}].total`
                    )) *
                    100
                );

                return ` ${label} : ${percentage} %`;
              },
              afterLabel: (tooltipItem, data) => "",
            },
          },
          legend: {
            display: false,
          },
          responsive: true,
          maintainAspectRatio: true,
        },
        legend: [
          {
            label:
              intl.messages[
                "visitorCharacteristicsScene.sections.qualificationVisitors.daily"
              ],
            color: ParkkiTheme.colors.red,
          },
          {
            label:
              intl.messages[
                "visitorCharacteristicsScene.sections.qualificationVisitors.monthly"
              ],
            color: ParkkiTheme.colors.darkOrange,
          },
          {
            label:
              intl.messages[
                "visitorCharacteristicsScene.sections.qualificationVisitors.weekly"
              ],
            color: ParkkiTheme.colors.orange,
          },
          {
            label:
              intl.messages[
                "visitorCharacteristicsScene.sections.qualificationVisitors.yearly"
              ],
            color: ParkkiTheme.colors.yellow,
          },
        ],
      },
    };
  };

  static getQualificationDestinationChart = (stats, areas, intl) => {
    if (
      !Abilities.ability.can(
        "read",
        "visitorCharacteristics.destinationsDistribution"
      )
    ) {
      return null;
    }

    const total = stats?.visits?.reduce(
      (previousValue, currentItem) => previousValue + currentItem?.nb_visits,
      0
    );
    const COLORS = [
      ParkkiTheme.colors.blue,
      ParkkiTheme.colors.lightBlue,
      ParkkiTheme.colors.flatRed,
      ParkkiTheme.colors.orange,
      ParkkiTheme.colors.darkOrange,
      ParkkiTheme.colors.turquoise2,
      ParkkiTheme.colors.lightGreen,
      ParkkiTheme.colors.green,
    ];

    const formattedResult = stats?.visits?.map((item, index) => {
      const currentArea = areas?.find((area) => area.id === item.area);

      return {
        label: currentArea?.long_name || currentArea?.short_name || item?.area,
        value: item?.nb_visits,
        percentage: (item?.nb_visits / total) * 100,
        color: COLORS[index],
      };
    });

    return formattedResult;
  };

  static getQualificationTimeLabels = () => {
    return new Array(24)
      .fill(0)
      .map((item, index) => `${index < 10 ? "0" : ""}${index}:00`);
  };

  static getQualificationTimeChart = (stats, intl) => {
    if (
      !Abilities.ability.can(
        "read",
        "visitorCharacteristics.parkingTimeDistribution"
      )
    ) {
      return null;
    }

    return {
      data: {
        toggle: Abilities.ability.can(
          "read",
          "visitorCharacteristics.parkingTimeDistribution"
        ),
        labels: VisitorCharacteristicsUtils.getQualificationTimeLabels(),
        datasets: [
          {
            type: "bar",
            label:
              intl.messages[
                "visitorCharacteristicsScene.sections.qualificationTime.title"
              ],
            yLabel: "analysis",
            data: get(stats, `data`, []).map((item) => get(item, "y")),
            backgroundColor: ParkkiTheme.colors.turquoise3,
            borderColor: ParkkiTheme.colors.turquoise3,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 12,
          },
        },
        title: {
          display: false,
        },
        tooltips: {
          enabled: true,
          mode: "index",
          callbacks: {
            title: (tooltipItems, data) => {
              if (tooltipItems.length !== 2) return "";

              const value = get(tooltipItems, "[1].yLabel");
              const compared = get(tooltipItems, "[0].yLabel");
              const percentage = Math.round(((compared - value) / value) * 100);
              if (get(data, "datasets", []).length === 1) {
                return "";
              }

              return `${percentage > 0 ? "+" : ""}${CommonUtils.formatNumber(
                percentage
              )} %`;
            },
            label: (
              tooltipItem = { datasetIndex: null, index: null },
              data = { datasets: [] }
            ) => {
              const { datasetIndex, index } = tooltipItem;
              const { datasets } = data;
              const currentDataset =
                datasetIndex >= 0 && datasets[datasetIndex];
              const value =
                (currentDataset &&
                  currentDataset.data &&
                  currentDataset.data[index]) ||
                intl.messages["parkStatisticsScene.chart.any"];

              return `${CommonUtils.humanizeDateFromDuration(
                value,
                "minutes",
                intl,
                false
              )} (${get(tooltipItem, "label")})`;
            },
          },
        },
        scales: {
          xAxes: [
            {
              categoryPercentage: 0.9,
              barPercentage: 0.95,
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                callback: (tickValue) => {
                  return (
                    CommonUtils.humanizeDateFromDuration(
                      tickValue,
                      "minutes",
                      intl,
                      false
                    ) ?? tickValue
                  );
                },
              },
            },
          ],
        },
      },
    };
  };
}

export default VisitorCharacteristicsUtils;
